import { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { clsx } from 'clsx';
import { HiOutlineChevronDown } from 'react-icons/hi2';
import { DoublePressFeedback, DoublePressLabel } from '../ButtonWithConfirm';
import { useDoublePress } from '../../hooks/useDoublePress';
import useForkRef from '../../hooks/useForkRef';
import { ARMED_SHORT_TEXT, armedTextOf, pressAgainTextOf } from '../../lib/doublePressText';
import Tooltip from '../../atoms/Tooltip';
import './TableToolbarButton.scss';

/** Пропсы якоря меню (`Menu`, `AnchoredDialog`): id для подписи меню, его состояние и открытие стрелками. */
type MenuTriggerProps = Pick<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'id' | 'onKeyDown' | 'aria-haspopup' | 'aria-expanded' | 'aria-controls'
>;

export interface ITableToolbarButtonProps extends MenuTriggerProps {
    ref?: Ref<HTMLButtonElement>;
    label: string;
    icon: ReactNode;
    /** Без обработчика — кнопка-триггер меню: его открывает обёртка (`Menu`). */
    onClick?: () => void;
    disabled?: boolean;
    'data-testid'?: string;
    /** Подпись рядом с иконкой: для главных действий таблицы («Запустить», «Пересканировать»). */
    showLabel?: boolean;
    /** Видимая подпись короче доступного имени: «Создать» при `label` «Создать подразделение» —
     *  объект понятен по списку, а полное имя остаётся в подсказке и у скринридера. */
    text?: string;
    /** `primary` — главное действие над списком («Создать»), залитая кнопка в начале тулбара;
     *  `danger` — деструктивное (удалить, очистить, прервать), узнаётся по цвету. */
    tone?: 'primary' | 'danger';
    /** Кнопка-переключатель: нажатое состояние (группировка, автообновление). */
    pressed?: boolean;
    /** Кнопка раскрывает панель (фильтр, столбцы): `aria-expanded` вместо `aria-pressed`, подсвечена,
     *  пока панель открыта. */
    expanded?: boolean;
    /** `id` панели, которую раскрывает кнопка (`aria-controls`). */
    controls?: string;
    /** Видимая подпись — живая область: скринридер объявляет её изменения («Выбрано: 3»). */
    liveText?: boolean;
    /** Идёт операция кнопки: вместо иконки — индикатор, кнопка недоступна. */
    loading?: boolean;
    /** Кнопка открывает меню (обёртка `Menu`): стрелка у подписи; `aria-expanded` и `aria-controls` ставит меню. */
    hasMenu?: boolean;
    /** Подсказка, если она длиннее подписи. */
    hint?: ReactNode;
    className?: string;
    /** Вопрос перед действием: кнопка в два нажатия — первое взводит её (подпись — «Точно?», вопрос «Точно <label>?» —
     *  подсказкой), второе выполняет. Сам вопрос объявляется скринридеру. */
    confirmationText?: ReactNode;
    /** Пояснение подсказкой у взведённой кнопки — то, чего нет на ней самой. */
    confirmationHint?: ReactNode;
    /** Вопрос взведённой кнопки; по умолчанию «Точно <label>?». */
    armedText?: string;
}

/**
 * Кнопка тулбара DataTable — квадрат в ряд со встроенными кнопками таблицы (обновить, экспорт, колонки)
 * или тот же квадрат с подписью. Одна высота, рамка и наведение во всех таблицах.
 */
const TableToolbarButton = ({
    label,
    icon,
    onClick,
    disabled,
    'data-testid': testId,
    showLabel,
    text,
    tone,
    pressed,
    expanded,
    controls,
    liveText,
    loading,
    hasMenu,
    hint,
    className,
    confirmationText,
    confirmationHint,
    armedText,
    ref,
    ...trigger
}: ITableToolbarButtonProps) => {
    const confirm = confirmationText != null;
    const press = useDoublePress<HTMLButtonElement>({ onConfirm: () => onClick?.(), disabled: disabled || loading });
    const buttonRef = useForkRef(ref, confirm ? press.rootRef : null);
    const armed = confirm && press.armed;
    const armedLabel = armedText ?? armedTextOf(label);
    const visibleText = text ?? label;
    const button = (
        <button
            ref={buttonRef}
            type="button"
            className={clsx(
                'dtb__icon-btn',
                showLabel && 'dtb__icon-btn--label',
                tone === 'primary' && 'dtb__icon-btn--primary',
                tone === 'danger' && 'table-toolbar-btn--danger',
                (pressed || expanded) && 'is-active',
                armed && 'table-toolbar-btn--armed',
                armed && tone === 'danger' && 'table-toolbar-btn--armed-danger',
                className,
            )}
            aria-label={armed ? armedLabel : label}
            aria-pressed={pressed}
            aria-busy={loading || undefined}
            aria-haspopup={hasMenu ? 'menu' : undefined}
            aria-expanded={expanded}
            aria-controls={controls}
            disabled={disabled || loading || press.pending}
            {...trigger}
            {...(confirm ? press.triggerProps : { onClick })}
            data-testid={testId}
        >
            {loading ? <span className="dtb__btn-spin" aria-hidden /> : icon}
            {showLabel && (
                <span aria-live={liveText ? 'polite' : undefined}>
                    {confirm ? (
                        <DoublePressLabel armed={armed} armedText={ARMED_SHORT_TEXT}>
                            {visibleText}
                        </DoublePressLabel>
                    ) : (
                        visibleText
                    )}
                </span>
            )}
            {showLabel && hasMenu && <HiOutlineChevronDown className="table-toolbar-btn__chevron" aria-hidden />}
        </button>
    );
    const feedback = confirm && (
        <DoublePressFeedback
            armed={armed}
            target={press.rootRef}
            announcement={
                <>
                    <span>{confirmationText} </span>
                    {confirmationHint != null && <span>{confirmationHint} </span>}
                    {pressAgainTextOf(label)}
                </>
            }
            title={armedLabel}
            hint={confirmationHint}
            tone={tone === 'danger' ? 'danger' : 'primary'}
        />
    );
    // у кнопки с подписью подсказка нужна, только если она что-то добавляет
    if (showLabel && !hint && visibleText === label) {
        return feedback ? (
            <>
                {button}
                {feedback}
            </>
        ) : (
            button
        );
    }
    return (
        <>
            {/* у взведённой кнопки своя подсказка — «Точно …?» */}
            <Tooltip openDelay={100} background="black" position="bottom" disabled={armed} label={hint ?? label}>
                {button}
            </Tooltip>
            {feedback}
        </>
    );
};

export default TableToolbarButton;
